// ... imports ...
import React, { useState, useEffect } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar
} from 'recharts';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Constants
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Interfaces
interface FinancialSummary {
    kpi: {
        totalSpend: number;
        arbitrageSavings: number;
        avgCostPerToken: number;
    };
    mainChart: Array<{ name: string; projected: number; actual: number }>;
    spendByDept: Array<{ name: string; value: number }>;
    budgetUtilization: Array<{ name: string; budget: number; used: number }>;
}

// Mock Data (Demo Mode)
const MOCK_DATA: FinancialSummary = {
    kpi: {
        totalSpend: 12400,
        arbitrageSavings: 4250,
        avgCostPerToken: 0.02
    },
    mainChart: [
        { name: 'Jan', projected: 5000, actual: 3800 },
        { name: 'Feb', projected: 5500, actual: 4200 },
        { name: 'Mar', projected: 6200, actual: 4600 },
        { name: 'Apr', projected: 6800, actual: 4900 },
        { name: 'May', projected: 7500, actual: 5200 },
        { name: 'Jun', projected: 8200, actual: 5600 },
    ],
    spendByDept: [
        { name: 'Marketing', value: 400 },
        { name: 'Dev', value: 300 },
        { name: 'HR', value: 300 },
    ],
    budgetUtilization: [
        { name: 'Marketing', budget: 100, used: 80 },
        { name: 'Dev', budget: 100, used: 45 },
        { name: 'HR', budget: 100, used: 25 },
    ]
};

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899']; // Emerald, Blue, Amber, Violet, Pink

const FinancePage: React.FC = () => {
    const token = localStorage.getItem("token");
    const [isDemoMode, setIsDemoMode] = useState(false);
    const [data, setData] = useState<FinancialSummary>(MOCK_DATA);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isDemoMode) {
            setData(MOCK_DATA);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}/metrics/financial/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const realData = await response.json();

                    // Transform backend response to match UI shape
                    const safeData: FinancialSummary = {
                        kpi: {
                            totalSpend: Number(realData.kpi?.totalSpend || 0),
                            arbitrageSavings: Number(realData.kpi?.arbitrageSavings || 0),
                            avgCostPerToken: Number(realData.kpi?.avgCostPerToken || 0),
                        },
                        mainChart: realData.mainChart || [],
                        spendByDept: realData.spendByDept || [],
                        budgetUtilization: realData.budgetUtilization || []
                    };

                    // Process main chart to ensure numbers
                    safeData.mainChart = safeData.mainChart.map((d: any) => ({
                        name: d.name,
                        actual: Number(d.actual),
                        projected: Number(d.projected)
                    }));

                    setData(safeData);
                } else {
                    console.error("Failed to fetch financial metrics");
                }
            } catch (error) {
                console.error("Error fetching metrics:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [isDemoMode, token]);

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-col gap-1">
                    <h2 className="text-3xl font-bold tracking-tight">Financial Overview</h2>
                    <p className="text-muted-foreground">Track spending, savings, and budget utilization.</p>
                </div>
                <div className="flex items-center space-x-2">
                    <Switch
                        id="demo-mode"
                        checked={isDemoMode}
                        onCheckedChange={setIsDemoMode}
                    />
                    <Label htmlFor="demo-mode">Demo Mode</Label>
                </div>
            </div>

            {/* Top KPI Row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* Card 1 */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
                        <Badge variant="outline" className="text-emerald-500 border-emerald-500/20 bg-emerald-500/10">All Time</Badge>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {isLoading ? "..." : `$${data.kpi.totalSpend.toFixed(2)}`}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {!isDemoMode ? "Actual usage cost" : "+12% from last month"}
                        </p>
                    </CardContent>
                </Card>

                {/* Card 2 - Highlighted */}
                <Card className="border-emerald-500/20 bg-emerald-500/10 dark:bg-emerald-950/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-emerald-500/20 blur-xl"></div>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Arbitrage Savings</CardTitle>
                        <Badge className="bg-emerald-500 hover:bg-emerald-600">Optimized</Badge>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {isLoading ? "..." : `$${data.kpi.arbitrageSavings.toFixed(2)}`}
                        </div>
                        <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80">
                            Estimated savings
                        </p>
                    </CardContent>
                </Card>

                {/* Card 3 */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Cost/Token</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {isLoading ? "..." : `$${data.kpi.avgCostPerToken.toFixed(6)}`}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Global average
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Chart: Projected vs Actual */}
            <Card className="col-span-4">
                <CardHeader>
                    <CardTitle>Usage Trends (Daily)</CardTitle>
                    <CardDescription>
                        {isDemoMode ? "Comparing projected vs actual costs." : "Recent daily spending."}
                    </CardDescription>
                </CardHeader>
                <CardContent className="pl-2">
                    <div className="h-[300px] w-full relative">
                        {data.mainChart.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={data.mainChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} stroke="hsl(var(--border))" />
                                    <XAxis
                                        dataKey="name"
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `$${value}`}
                                    />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--popover-foreground))' }}
                                        itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="projected"
                                        stroke="hsl(var(--muted-foreground))"
                                        strokeWidth={2}
                                        fill="transparent"
                                        strokeDasharray="5 5"
                                        name="Projected"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="actual"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        fill="url(#colorSavings)"
                                        name="Actual"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex h-full items-center justify-center text-muted-foreground">
                                No data available
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Bottom Widgets */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Spend by Model/Dept */}
                <Card>
                    <CardHeader>
                        <CardTitle>Spend Distribution</CardTitle>
                        <CardDescription>Cost breakdown by model type.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {data.spendByDept.length > 0 ? (
                            <>
                                <div className="flex-1 min-h-[250px] flex items-center justify-center">
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={data.spendByDept}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {data.spendByDept.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--popover-foreground))' }}
                                                itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                                            />
                                            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-lg font-bold">
                                                ${data.kpi.totalSpend.toFixed(0)}
                                            </text>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="mt-4 flex flex-wrap justify-center gap-4">
                                    {data.spendByDept.map((entry, index) => (
                                        <div key={entry.name} className="flex items-center gap-2">
                                            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                            <span className="text-sm text-muted-foreground">{entry.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="flex h-[250px] items-center justify-center text-muted-foreground">
                                No data available
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Budget Utilization */}
                <Card>
                    <CardHeader>
                        <CardTitle>Budget Usage</CardTitle>
                        <CardDescription>Utilization vs Limit.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex-1 min-h-[250px]">
                            {data.budgetUtilization.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        layout="vertical"
                                        data={data.budgetUtilization}
                                        margin={{ top: 0, right: 30, left: 40, bottom: 5 }}
                                        barSize={24}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" stroke="hsl(var(--border))" />
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            stroke="hsl(var(--muted-foreground))"
                                            fontSize={14}
                                            tickLine={false}
                                            axisLine={false}
                                            width={80}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'transparent' }}
                                            contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--popover-foreground))' }}
                                            itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                                        />
                                        <Bar dataKey="value" name="Used (Amount)" radius={[0, 4, 4, 0]}>
                                            {data.budgetUtilization.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.used && entry.used > 80 ? '#ef4444' : '#10b981'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex h-full items-center justify-center text-muted-foreground">
                                    No data available
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default FinancePage;
